import type { SiteLang } from "@/hooks/useLanguage";
import type { OfferingSection } from "@/components/services/GenericOfferingContent";

/* ─────────────────────────────────────────────────────
   Spanish translations for ToxOffering page metadata
   ───────────────────────────────────────────────────── */

interface OfferingPageMeta {
  title: string;
  subtitle: string;
  description: string;
  faqItems?: { q: string; a: string }[];
}

const offeringMetaES: Record<string, OfferingPageMeta> = {
  "pde-oel-reports": {
    title: "Informes PDE-OEL",
    subtitle: "Soporte toxicológico especializado respaldado por expertos acreditados",
    description:
      "QbD Group prepara informes PDE/ADE para cualquier principio activo farmacéutico conforme a las directivas de la EMA. Nuestros toxicólogos certificados ERT ofrecen evaluaciones científicamente sólidas para las industrias farmacéutica y veterinaria.",
    faqItems: [
      { q: "¿Qué es un informe PDE?", a: "Un informe PDE (Exposición Diaria Permitida) establece la exposición máxima aceptable diaria a una sustancia específica, basándose en una evaluación toxicológica exhaustiva del compuesto." },
      { q: "¿Cuántos compuestos ha evaluado QbD?", a: "Nuestro equipo ha evaluado más de 3.500 compuestos para informes PDE/ADE, atendiendo a más de 600 clientes en más de 50 países." },
      { q: "¿Quién prepara y revisa los informes?", a: "Todos los informes son preparados y revisados por toxicólogos certificados ERT con amplia experiencia en toxicología farmacéutica." },
    ],
  },
  "nitrosamine-risk-assessment": {
    title: "Evaluación del riesgo de las nitrosaminas",
    subtitle: "Navega las complejidades regulatorias de las nitrosaminas con orientación experta",
    description:
      "QbD Group se especializa en evaluaciones de riesgo de nitrosaminas, ayudando a las empresas farmacéuticas a identificar riesgos, redactar informes, calcular límites aceptables y responder a las agencias regulatorias.",
    faqItems: [
      { q: "¿Qué son las nitrosaminas y por qué son preocupantes?", a: "Las nitrosaminas son compuestos químicos que pueden suponer un riesgo de cáncer. Las agencias regulatorias exigen que su presencia en productos farmacéuticos se minimice o elimine." },
      { q: "¿Qué productos requieren estas evaluaciones?", a: "Según la EMA: los titulares de autorizaciones de comercialización de todos los medicamentos de uso humano deben evaluar el riesgo de presencia de impurezas de nitrosaminas en sus principios activos y productos terminados." },
      { q: "¿Cuánto tiempo lleva la evaluación de riesgos?", a: "Dependiendo de la complejidad, las evaluaciones suelen llevar de 4 a 8 semanas." },
      { q: "¿Qué ocurre si existe riesgo de impurezas de nitrosaminas?", a: "Si se identifica un riesgo, el primer paso son pruebas confirmatorias para determinar experimentalmente los niveles de nitrosaminas. Según los resultados, se tomarán las acciones apropiadas." },
      { q: "¿Qué directrices regulatorias seguís?", a: "Seguimos las directrices de la EMA y la FDA específicas para nitrosaminas, los principios ICH Q9 para evaluación de riesgos, ICH M7 y otras regulaciones globales." },
      { q: "¿Es posible mitigar el riesgo utilizando estrategias basadas en QSAR?", a: "No, los datos in silico no son aceptados actualmente para determinar límites o evaluar el grado de formación de nitrosaminas." },
    ],
  },
  "extractables-leachables-reports": {
    title: "Informes sobre extractables y lixiviables",
    subtitle: "Evaluaciones toxicológicas expertas para la seguridad de envases y dispositivos",
    description:
      "QbD Group proporciona evaluaciones toxicológicas y de riesgo para impurezas extractables y lixiviables, diseñadas para adaptarse a tus necesidades específicas y garantizar el cumplimiento de los estándares de la FDA, EMA e ICH.",
    faqItems: [
      { q: "¿Por qué son importantes los estudios de extractables y lixiviables?", a: "Estos estudios aseguran que las impurezas no comprometan la seguridad, eficacia o calidad del producto, cumpliendo los requisitos regulatorios internacionales." },
      { q: "¿Qué regulaciones se aplican a extractables y lixiviables?", a: "Las directrices de la FDA, EMA, USP 1663, USP 1664 e ICH Q3E exigen evaluaciones rigurosas de estas impurezas." },
      { q: "¿Cuándo deben realizarse estos estudios?", a: "Deben realizarse durante el desarrollo del producto y antes de la comercialización." },
      { q: "¿Qué productos requieren estas evaluaciones?", a: "Todos los productos farmacéuticos en contacto con plásticos, caucho, metales u otros componentes de envase y dispositivos." },
    ],
  },
  "environmental-risk-assessment-era": {
    title: "Evaluación del riesgo medioambiental (ERA)",
    subtitle: "Informes ERA obligatorios alineados con las expectativas regulatorias de la EMA",
    description:
      "QbD Group apoya a las empresas farmacéuticas a lo largo de todo el ciclo de vida del ERA, combinando experiencia científica con un enfoque práctico y basado en riesgos para entregar documentación lista para su presentación en solicitudes de autorización de comercialización.",
  },
  "elemental-impurities-reports": {
    title: "Informes sobre impurezas elementales",
    subtitle: "Evaluaciones de riesgos integrales para el cumplimiento farmacéutico",
    description:
      "QbD Group proporciona evaluaciones de riesgos e informes integrales sobre impurezas elementales en productos farmacéuticos de uso humano y veterinario, asegurando el cumplimiento de las directrices ICH Q3D y los requisitos regulatorios.",
  },
  "organic-impurities-reports": {
    title: "Informes sobre impurezas orgánicas",
    subtitle: "Informes de toxicología certificados para evaluaciones de impurezas",
    description:
      "QbD Group ofrece la mayor biblioteca online de informes de toxicología de alta calidad certificados por miembros de AETOX, que cubren evaluaciones de riesgos y evaluaciones toxicológicas de impurezas orgánicas en la fabricación farmacéutica.",
  },
  "residual-solvents-reports": {
    title: "Informes sobre disolventes residuales",
    subtitle: "Evaluaciones de riesgo para disolventes residuales en productos farmacéuticos",
    description:
      "QbD Group se especializa en evaluaciones de riesgo de disolventes residuales en productos farmacéuticos de uso humano y veterinario, ayudando a identificar riesgos, generar informes integrales y abordar las preocupaciones regulatorias según la directriz ICH Q3C.",
  },
  "reach-registration-compliance-support": {
    title: "Registro REACH y apoyo al cumplimiento",
    subtitle: "Navega el registro REACH con orientación experta",
    description:
      "QbD Group te ayuda a navegar cada paso del registro REACH, desde la caracterización de sustancias y la elaboración del dossier IUCLID hasta la redacción de FDS y la elaboración del Informe de Seguridad Química.",
  },
  "monitoring-studies": {
    title: "Estudios de seguimiento",
    subtitle: "Soporte experto para estudios de laboratorio y cumplimiento regulatorio",
    description:
      "QbD Group proporciona soporte experto para el seguimiento de estudios de laboratorio requeridos en la fabricación y calidad de medicamentos, incluyendo validación de métodos, revisión de resultados y redacción de informes para presentaciones regulatorias.",
  },
};

const offeringMetaZh: Record<string, OfferingPageMeta> = {
  "residual-solvents-reports": {
    title: "残留溶剂报告",
    subtitle: "药品中残留溶剂的风险评估",
    description:
      "QbD Group 专注于人用和兽用药品中残留溶剂的风险评估，帮助识别风险、生成全面报告，并依据 ICH Q3C 指南应对法规关切。",
  },
  "reach-registration-compliance-support": {
    title: "REACH 注册与合规支持",
    subtitle: "在专家指导下完成 REACH 注册",
    description:
      "QbD Group 协助您完成 REACH 注册的每一步，从物质表征、IUCLID 卷宗编制，到安全数据表(SDS)起草和化学品安全报告(CSR)撰写。",
  },
  "pde-oel-reports": {
    title: "PDE-OEL 报告",
    subtitle: "由权威专家提供的专业毒理学支持",
    description:
      "QbD Group 依据 EMA 指南为任何活性药物成分编制 PDE/ADE 报告。我们的 ERT 认证毒理学家为制药和兽药行业提供科学严谨的评估。",
    faqItems: [
      { q: "什么是 PDE 报告？", a: "PDE(每日允许暴露量)报告基于对化合物的全面毒理学评估，确定特定物质每日可接受的最大暴露量。" },
      { q: "QbD 评估过多少种化合物？", a: "我们的团队已为 PDE/ADE 报告评估了 3,500 多种化合物，服务遍及 50 多个国家的 600 多家客户。" },
      { q: "由谁编制和审核报告？", a: "所有报告均由在制药毒理学领域拥有丰富经验的 ERT 认证毒理学家编制和审核。" },
    ],
  },
  "organic-impurities-reports": {
    title: "有机杂质报告",
    subtitle: "用于杂质评估的认证毒理学报告",
    description:
      "QbD Group 提供由 AETOX 成员认证的高质量毒理学报告在线库，涵盖药品制造中有机杂质的风险评估和毒理学评估。",
  },
  "nitrosamine-risk-assessment": {
    title: "亚硝胺风险评估",
    subtitle: "在专家指导下应对亚硝胺法规复杂性",
    description:
      "QbD Group 专注于亚硝胺风险评估，帮助制药企业识别风险、撰写报告、计算可接受限度，并回应监管机构的要求。",
    faqItems: [
      { q: "什么是亚硝胺，为什么令人担忧？", a: "亚硝胺是可能致癌的化学化合物。监管机构要求将其在药品中的含量降至最低或完全消除。" },
      { q: "哪些产品需要进行此类评估？", a: "根据 EMA 要求：所有人用药品的上市许可持有人(MAH)/申请人均需评估其原料药(API)和成品(FP)中亚硝胺杂质存在的风险。" },
      { q: "风险评估需要多长时间？", a: "根据复杂程度，评估通常需要 4-8 周。" },
      { q: "如果存在亚硝胺杂质风险该怎么办？", a: "如果识别出风险，第一步是进行确认性检测，以实验确定亚硝胺水平。根据结果，将采取适当措施。" },
      { q: "你们遵循哪些法规指南？", a: "我们遵循 EMA 和 FDA 针对亚硝胺的具体指南、ICH Q9 风险评估原则、ICH M7 及其他全球法规。" },
      { q: "是否可以使用基于 QSAR 的策略来降低风险？", a: "不可以，目前不接受使用计算机模拟(in-silico)数据来确定限度或评估亚硝胺形成的程度。" },
    ],
  },
  "monitoring-studies": {
    title: "监测研究",
    subtitle: "为实验室研究和法规合规提供专业支持",
    description:
      "QbD Group 为药品生产和质量所需的实验室研究跟踪提供专业支持，包括方法验证、结果审核以及用于法规申报的报告撰写。",
  },
  "extractables-leachables-reports": {
    title: "可提取物与浸出物报告",
    subtitle: "包装与器械安全的专家毒理学评估",
    description:
      "QbD Group 为可提取物和浸出物杂质提供毒理学和风险评估，量身定制以满足您的特定需求，并确保符合 FDA、EMA 和 ICH 标准。",
    faqItems: [
      { q: "为什么可提取物与浸出物研究很重要？", a: "这些研究确保杂质不会影响产品的安全性、有效性或质量，同时满足国际法规要求。" },
      { q: "哪些法规适用于可提取物与浸出物？", a: "FDA、EMA、USP 1663、USP 1664 和 ICH Q3E 指南均要求对这些杂质进行严格评估。" },
      { q: "这些研究应在何时进行？", a: "应在产品开发期间和上市前进行。" },
      { q: "哪些产品需要这些评估？", a: "所有与塑料、橡胶、金属或其他包装材料和器械组件接触的药品。" },
    ],
  },
  "environmental-risk-assessment-era": {
    title: "环境风险评估 (ERA)",
    subtitle: "符合 EMA 法规期望的强制性 ERA 报告",
    description:
      "QbD Group 在 ERA 全生命周期为制药企业提供支持，将科学专业知识与务实、基于风险的方法相结合，交付可直接用于上市许可申请的申报文件。",
  },
  "elemental-impurities-reports": {
    title: "元素杂质报告",
    subtitle: "全面的药品合规风险评估",
    description:
      "QbD Group 为人用和兽用药品中的元素杂质提供全面的风险评估和报告，确保符合 ICH Q3D 指南和法规要求。",
  },
};

export const getToxOfferingMeta = (
  contentKey: string,
  lang: SiteLang,
): OfferingPageMeta | undefined => {
  if (lang === "es") return offeringMetaES[contentKey];
  if (lang === "zh-cn") return offeringMetaZh[contentKey];
  return undefined;
};

/* ─────────────────────────────────────────────────────
   Full section-level Spanish content overrides
   ───────────────────────────────────────────────────── */

type OfferingSectionTextOverride = {
  whatTitle: string;
  whatParagraphs: string[];
  whatImageAlt?: string;
  whyTitle: string;
  whyIntro?: string;
  whyBenefits: { title: string; text: string }[];
  helpCards?: {
    heading: string;
    intro: string;
    cards: { title: string; text: string }[];
  };
  extraSections?: { title: string; paragraphs: string[]; bullets?: { title: string; text: string }[] }[];
  processTitle?: string;
  processIntro?: string;
  processSteps?: { title: string; text: string }[];
  processImageAlt?: string;
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
};

const offeringContentES: Record<string, OfferingSectionTextOverride> = {
  /* ───────── PDE-OEL Reports ───────── */
  "pde-oel-reports": {
    whatTitle: "Informes PDE/ADE adaptados a los estándares de la industria",
    whatParagraphs: [
      "Nuestro equipo altamente cualificado prepara <strong class='text-foreground'>informes PDE/ADE (Exposición Diaria Permitida/Exposición Diaria Aceptable)</strong> para cualquier principio activo farmacéutico (API) conforme a la <strong class='text-foreground'>directiva de la EMA (EMA/CHMP/CVMP/SWP/169430/2012)</strong>. Estos informes son esenciales para las industrias farmacéutica y veterinaria para garantizar la seguridad del producto y el cumplimiento normativo.",
      "Deja que QbD Group te guíe hacia valores PDE precisos y fiables con evaluaciones científicamente sólidas.",
    ],
    whyTitle: "¿Por qué elegir QbD Group para tus informes PDE?",
    whyIntro: "Cuando se trata de informes PDE/ADE, necesitas un socio que combine experiencia científica, trayectoria probada y compromiso con la calidad.",
    whyBenefits: [
      { title: "Amplia experiencia", text: "Más de 3.500 compuestos evaluados." },
      { title: "Confianza global", text: "Más de 600 clientes satisfechos en todo el mundo." },
      { title: "Alcance internacional", text: "Experiencia entregada en más de 50 países." },
      { title: "Equipo altamente cualificado", text: "Preparados y revisados por toxicólogos certificados ERT con años de experiencia." },
      { title: "Evaluaciones sólidas", text: "Evaluaciones científicamente fundamentadas combinadas con juicio experto." },
      { title: "Análisis exhaustivo", text: "Discusión detallada que respalda la derivación de valores PDE." },
      { title: "Resultados fiables", text: "Valores PDE basados en evidencia, precisos y fiables adaptados a tus necesidades." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque estructurado para entregar informes PDE/ADE que cumplen los más altos estándares de calidad científica y cumplimiento normativo.",
    processSteps: [
      { title: "1. Consulta inicial", text: "Comprender tus necesidades específicas y preocupaciones regulatorias." },
      { title: "2. Recopilación de datos", text: "Reunir la documentación necesaria sobre el compuesto y su perfil toxicológico." },
      { title: "3. Evaluación toxicológica", text: "Revisión bibliográfica exhaustiva y evaluación experta para derivar valores PDE." },
      { title: "4. Entrega del informe", text: "Proporcionar un informe PDE/ADE detallado con justificación científica completa." },
      { title: "5. Soporte de seguimiento", text: "Asistencia continua para consultas regulatorias o actualizaciones." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestros informes son preparados y revisados por toxicólogos certificados EUROTOX (ERT), asegurando los más altos estándares de calidad científica, fiabilidad regulatoria y seguridad del paciente.",
    partnerBullets: [
      "Soporte toxicológico integral — desde el desarrollo temprano hasta los cambios post-aprobación.",
      "Experiencia de confianza a escala — décadas de experiencia combinada en proyectos y mercados diversos.",
      "Supervisión proactiva del cumplimiento — control estricto para anticiparse a las regulaciones en evolución.",
      "Toxicólogos certificados — certificación EUROTOX (ERT) en cada proyecto.",
    ],
  },

  /* ───────── Nitrosamine Risk Assessment ───────── */
  "nitrosamine-risk-assessment": {
    whatTitle: "¿Qué es la evaluación del riesgo de nitrosaminas?",
    whatParagraphs: [
      "La evaluación del riesgo de nitrosaminas es una <strong class='text-foreground'>evaluación exhaustiva de los procesos de fabricación de productos farmacéuticos</strong>, dirigida a identificar y mitigar los riesgos asociados con las impurezas de nitrosaminas. Implica analizar materias primas, excipientes, equipos y procesos de fabricación.",
      "Este proceso se adhiere a las directrices regulatorias globales (EMA, FDA), <strong class='text-foreground'>asegurando que todos los niveles detectados de nitrosaminas se mantengan dentro de los límites aceptables</strong>. Es un requisito esencial para los titulares de autorizaciones de comercialización (MAH), fabricantes de API, fabricantes de productos terminados y proveedores.",
    ],
    whyTitle: "¿Por qué es importante?",
    whyIntro: "Las impurezas de nitrosaminas son una preocupación de seguridad significativa debido a su alto potencial carcinogénico, clasificándolas como un \"grupo de especial preocupación\" entre las impurezas mutagénicas.",
    whyBenefits: [
      { title: "Alto potencial carcinogénico", text: "Las nitrosaminas suponen riesgos significativos de cáncer y están sujetas a límites regulatorios estrictos." },
      { title: "Cumplimiento normativo", text: "Las empresas deben identificar, evaluar y mitigar riesgos para cumplir con las directrices de la EMA, FDA e ICH." },
      { title: "Evitar retiradas costosas", text: "Una evaluación adecuada previene sanciones por incumplimiento, retiradas de productos o retrasos." },
      { title: "Revisión regulatoria continua", text: "Nuevos datos continúan surgiendo, requiriendo evaluaciones actualizadas." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "Sea cual sea tu necesidad en evaluación de riesgo de nitrosaminas (NRA), disponemos de diferentes servicios para apoyarte:",
      cards: [
        { title: "Evaluación de riesgos", text: "Evaluación integral de los procesos de fabricación, abordando todos los factores de riesgo y causas raíz descritos en las directrices de la EMA y la FDA. Actualizaciones del NRA cuando surja nueva información que deba reflejarse en el informe." },
        { title: "Cálculo de límites aceptables", text: "Asistencia en la determinación de límites aceptables basados en cálculos de ingesta aceptable (AI) para las impurezas de nitrosaminas identificadas como riesgos potenciales. Cubre nitrosaminas listadas por la EMA y FDA, así como nitrosaminas desconocidas." },
        { title: "Revisión de informes NRA", text: "Evaluación de informes NRA preparados por otras empresas o documentación relacionada con impurezas de nitrosaminas en componentes farmacéuticos para asegurar el cumplimiento de los requisitos de las agencias regulatorias." },
        { title: "Soporte experto", text: "Apoyo en la implementación de acciones derivadas de los resultados de la evaluación de riesgo de nitrosaminas, asegurando que se tomen las medidas de control adecuadas para abordar eficazmente los riesgos identificados." },
        { title: "Estrategias de priorización", text: "Análisis de procesos de fabricación de varios productos farmacéuticos para identificar los más susceptibles a riesgos de impurezas de nitrosaminas y proponer estrategias de priorización." },
        { title: "Informe de evaluación toxicológica de nitrosaminas", text: "Evaluación toxicológica, incluyendo revisión de datos de genotoxicidad/carcinogenicidad y establecimiento de ingesta aceptable utilizando el valor TD50 de un sustituto con datos de carcinogenicidad suficientemente robustos." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque estructurado, paso a paso, para asegurar una gestión integral del riesgo de nitrosaminas.",
    processSteps: [
      { title: "1. Consulta inicial", text: "Comprender las necesidades específicas del cliente y sus preocupaciones regulatorias." },
      { title: "2. Recopilación de datos", text: "Reunir documentación sobre el proceso de fabricación del producto, incluyendo declaraciones del fabricante." },
      { title: "3. Informe de evaluación de riesgos", text: "Preparar un informe NRA identificando fuentes potenciales de contaminación por nitrosaminas." },
      { title: "4. Entrega del informe", text: "Informe detallado sobre si existe riesgo de presencia de nitrosaminas." },
      { title: "5. Soporte de seguimiento", text: "Asistencia continua incluyendo cálculos de límites aceptables para pruebas confirmatorias." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestros informes son preparados y revisados por toxicólogos certificados EUROTOX (ERT), asegurando los más altos estándares de calidad científica y fiabilidad regulatoria.",
    partnerBullets: [
      "Soporte toxicológico integral — evaluaciones de riesgo integradas alineadas con estrategias de QA, CMC y regulatorias.",
      "Experiencia de confianza a escala — insights fiables en proyectos y mercados diversos.",
      "Supervisión proactiva del cumplimiento — anticipándose a las regulaciones de nitrosaminas en evolución.",
      "Toxicólogos certificados — certificación EUROTOX (ERT) en cada evaluación.",
    ],
  },

  /* ───────── Extractables & Leachables ───────── */
  "extractables-leachables-reports": {
    whatTitle: "¿Qué son los informes de extractables y lixiviables?",
    whatParagraphs: [
      "La evaluación de riesgos y toxicológica de impurezas extractables y lixiviables es un proceso crucial en la industria farmacéutica para garantizar la seguridad y calidad de los productos.",
      "Estas impurezas, que pueden migrar desde materiales de envase o dispositivos médicos al producto final, <strong class='text-foreground'>representan un riesgo potencial para los pacientes</strong>. Reguladores como la FDA y la EMA <strong class='text-foreground'>exigen informes detallados</strong> para respaldar la seguridad de medicamentos y dispositivos médicos.",
    ],
    whyTitle: "¿Cómo podemos ayudar?",
    whyIntro: "Nuestro equipo de toxicólogos y químicos analíticos trabaja conjuntamente para realizar análisis en profundidad, evaluaciones de riesgos y evaluaciones toxicológicas.",
    whyBenefits: [
      { title: "Evaluación de riesgos", text: "Análisis de posibles compuestos liberados de materiales en contacto con el medicamento, evaluando su impacto en la seguridad y determinando límites seguros." },
      { title: "Evaluación toxicológica", text: "Para todas las impurezas fuera de los límites establecidos — recopilación de efectos dañinos, determinación de toxicidad y evaluación de riesgos para la salud mediante métodos QSAR." },
      { title: "Informes personalizados", text: "Informes adaptados a los requisitos regulatorios específicos de cada producto farmacéutico y cliente." },
      { title: "Alineación regulatoria", text: "Cumplimiento con las directrices de la FDA, EMA, USP 1663, USP 1664 e ICH Q3E." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "Proporcionamos informes personalizados que cumplen los requisitos regulatorios específicos para cada producto farmacéutico y cliente.",
    processSteps: [
      { title: "1. Consulta inicial", text: "Comprender tus materiales específicos y el contexto regulatorio." },
      { title: "2. Análisis de materiales", text: "Identificar posibles compuestos extractables y lixiviables de materiales de envase y dispositivos." },
      { title: "3. Evaluación de riesgos y toxicológica", text: "Evaluar el impacto en la seguridad y determinar si las impurezas están dentro de los límites seguros." },
      { title: "4. Preparación del informe", text: "Entregar informes integrales, listos para presentación, alineados con los estándares regulatorios." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Combinamos un profundo conocimiento técnico con una comprensión detallada de las regulaciones internacionales, asegurando informes personalizados y rigurosos.",
    partnerBullets: [
      "Equipo especializado y multidisciplinar de toxicólogos y químicos analíticos.",
      "Informes personalizados según los requisitos regulatorios y necesidades específicas de cada cliente.",
      "Enfoque que combina un profundo conocimiento técnico con la comprensión de las regulaciones internacionales.",
      "Miembros de AETOX y certificación EUROTOX — los más altos estándares profesionales.",
    ],
  },

  /* ───────── ERA ───────── */
  "environmental-risk-assessment-era": {
    whatTitle: "El papel de la evaluación de riesgos medioambientales en la autorización de comercialización",
    whatParagraphs: [
      "Las evaluaciones de riesgo medioambiental no son complementos opcionales; son un <strong class='text-foreground'>requisito obligatorio</strong> en muchas regiones, especialmente para nuevas solicitudes de autorización de comercialización (MAA) donde la exposición ambiental puede cambiar.",
      "Un ERA ayuda a los reguladores a <strong class='text-foreground'>evaluar la probabilidad de daño a los ecosistemas acuáticos y terrestres</strong> basándose en concentraciones ambientales previstas y datos ecotoxicológicos.",
      "Cuando se completan correctamente, los informes ERA proporcionan una justificación transparente de las estimaciones de exposición y las decisiones de pruebas, <strong class='text-foreground'>ayudan a evitar estudios innecesarios y facilitan interacciones regulatorias más fluidas</strong>. Consolidar datos de literatura, estudios de destino y evaluaciones de exposición refinadas en un documento coherente listo para presentación es clave para cumplir las expectativas regulatorias y evitar retrasos.",
    ],
    whyTitle: "Cómo QbD Group apoya tu evaluación de riesgo medioambiental",
    whyIntro: "Soporte ERA estructurado e impulsado por la ciencia, alineado con las expectativas regulatorias. Apoyamos a las empresas farmacéuticas a lo largo de todo el ciclo de vida del ERA, combinando experiencia científica con un enfoque práctico y basado en riesgos.",
    whyBenefits: [
      { title: "Recopilación exhaustiva de datos", text: "Cubriendo datos de uso, propiedades fisicoquímicas, destino ambiental y ecotoxicología." },
      { title: "Evaluación de exposición y riesgo", text: "Cálculo y refinamiento de PEC, derivación de PNEC y cribado PBT/vPvB." },
      { title: "Justificación científica", text: "Respaldando evaluaciones dirigidas, exenciones de pruebas o refinamientos conforme a la guía de la EMA." },
      { title: "Informes ERA claros y estructurados", text: "Diseñados para inclusión directa en el Módulo 1.6 del CTD y alineados con las expectativas regulatorias." },
    ],
    extraSections: [
      {
        title: "Del alcance inicial a informes ERA listos para presentación",
        paragraphs: [
          "Comenzamos con un análisis de alcance temprano para alinear el API, mercados objetivo, indicación y datos disponibles, <strong class='text-foreground'>asegurando que la estrategia ERA se ajuste al contexto regulatorio desde el inicio</strong>.",
          "Nuestro equipo aplica un <strong class='text-foreground'>enfoque por etapas</strong>: cálculos PEC iniciales de Tier A, refinamiento utilizando datos de prevalencia y consumo específicos por país, e integración de literatura y datos históricos para respaldar conclusiones científicamente sólidas. Cuando es apropiado, preparamos <strong class='text-foreground'>justificaciones claras para eximir estudios no relevantes</strong> o evitar la escalada a niveles de evaluación superiores.",
          "Cada proyecto concluye con un <strong class='text-foreground'>informe ERA listo para presentación</strong>, completamente trazable, documentado de forma transparente y preparado para resistir la revisión regulatoria.",
        ],
      },
      {
        title: "Valor entregado a nuestros clientes",
        paragraphs: [],
        bullets: [
          { title: "Confianza regulatoria", text: "Informes ERA estructurados conforme a la guía de la EMA, facilitando una revisión y aceptación fluidas del dossier." },
          { title: "Reducción de pruebas innecesarias", text: "Uso estratégico de datos existentes, refinamientos y justificación científica para evitar estudios irrelevantes." },
          { title: "Eficiencia y previsibilidad", text: "Flujos de trabajo claros e identificación temprana de riesgos que ayudan a prevenir sorpresas y retrasos en fases avanzadas." },
          { title: "Documentación lista para inspección", text: "Cálculos transparentes, referenciación completa y toma de decisiones trazable alineada con el escrutinio regulatorio." },
        ],
      },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque por etapas, impulsado por la ciencia, alineado con la guía de la EMA para entregar informes ERA listos para presentación.",
    processSteps: [
      { title: "1. Alcance inicial", text: "Alinear API, mercados objetivo, indicación y datos disponibles." },
      { title: "2. Recopilación de datos", text: "Evaluación técnica exhaustiva de datos de uso, fisicoquímicos, de destino y ecotoxicológicos." },
      { title: "3. Evaluación de exposición", text: "Cálculos PEC, refinamientos y derivación de PNEC." },
      { title: "4. Preparación del informe", text: "Informe ERA listo para presentación para el Módulo 1.6 del CTD." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestro equipo certificado EUROTOX ofrece soporte ERA eficiente e impulsado por la ciencia, alineado con las expectativas regulatorias.",
    partnerBullets: [
      "Soporte toxicológico integral — desde el desarrollo temprano hasta los cambios post-aprobación, proporcionamos evaluaciones de riesgo integradas alineadas con estrategias de QA, CMC y regulatorias.",
      "Experiencia de confianza a escala — con décadas de experiencia combinada en proyectos y mercados diversos, nuestro equipo de toxicología proporciona insights fiables que añaden eficiencia, consistencia y confianza a cada evaluación.",
      "Supervisión proactiva del cumplimiento — mediante un control estricto de los informes, ayudamos a los clientes a mantener el control total de las obligaciones toxicológicas, alertándoles proactivamente sobre posibles no conformidades antes de que se conviertan en riesgos.",
      "Toxicólogos certificados — nuestros informes son preparados y revisados por toxicólogos certificados EUROTOX (ERT), asegurando los más altos estándares de calidad científica, fiabilidad regulatoria y seguridad del paciente en cada evaluación.",
    ],
  },

  /* ───────── Elemental Impurities ───────── */
  "elemental-impurities-reports": {
    whatTitle: "¿Qué es la evaluación de riesgo de impurezas elementales?",
    whatParagraphs: [
      "Asegurar el cumplimiento de la regulación de impurezas elementales comienza con una evaluación de riesgos exhaustiva de tus procesos de fabricación farmacéutica. En QbD Group, te guiamos a través de los <strong class='text-foreground'>cuatro pasos esenciales de la evaluación de riesgos</strong>:",
      "<ol class='list-decimal pl-6 mt-3 space-y-2'><li>Identificar las fuentes potenciales de impurezas elementales.</li><li>Analizar los elementos introducidos por cada fuente de contaminación.</li><li>Evaluar los niveles reales o previstos en comparación con los valores PDE establecidos.</li><li>Definir una estrategia de control basada en los resultados.</li></ol>",
      "Nuestro equipo experto asegura un enfoque integral para ayudarte a cumplir los estándares regulatorios mientras protege la calidad del producto y la seguridad del paciente.",
    ],
    whyTitle: "Por qué importa el control de impurezas elementales",
    whyIntro: "Las impurezas elementales no ofrecen beneficio terapéutico y deben controlarse dentro de los límites aceptables, según las directrices ICH Q3D.",
    whyBenefits: [
      { title: "Requisito regulatorio", text: "Obligatorio para solicitudes de autorización de comercialización (MAA) e inspecciones GMP." },
      { title: "Seguridad del paciente", text: "Controlar las impurezas protege a los pacientes de riesgos potenciales para la salud." },
      { title: "Preparación para auditorías", text: "Las evaluaciones de riesgo documentadas son obligatorias para auditorías e inspecciones." },
      { title: "Dossiers de registro", text: "Los informes deben incluirse en las presentaciones regulatorias." },
    ],
    processTitle: "De la consulta al cumplimiento: nuestro proceso paso a paso",
    processIntro: "Nuestro enfoque estructurado asegura una evaluación fluida y conforme de las impurezas elementales en tus productos farmacéuticos.",
    processSteps: [
      { title: "1. Consulta inicial", text: "Comprender tus necesidades específicas y preocupaciones regulatorias para adaptar nuestro enfoque." },
      { title: "2. Recopilación de datos", text: "Reunir documentación incluyendo detalles del proceso de fabricación y declaraciones del fabricante." },
      { title: "3. Informe de evaluación de riesgos", text: "Informe detallado identificando fuentes de contaminación y evaluando riesgos según las directrices de la EMA/FDA." },
      { title: "4. Entrega del informe", text: "Informe completo con niveles de impurezas, comparaciones PDE y recomendaciones de control." },
      { title: "5. Soporte de seguimiento", text: "Asistencia continua incluyendo cálculos de límites aceptables y recomendaciones de límites de detección." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestro equipo certificado EUROTOX asegura los más altos estándares de calidad científica y fiabilidad regulatoria en cada evaluación de impurezas elementales.",
    partnerBullets: [
      "Soporte toxicológico integral — desde el desarrollo temprano hasta los cambios post-aprobación.",
      "Experiencia de confianza a escala — décadas de experiencia combinada en proyectos diversos.",
      "Supervisión proactiva del cumplimiento — anticipándose a las regulaciones en evolución.",
      "Toxicólogos certificados — certificación EUROTOX (ERT) en cada proyecto.",
    ],
  },

  /* ───────── Organic Impurities ───────── */
  "organic-impurities-reports": {
    whatTitle: "¿Por qué es necesario?",
    whatParagraphs: [
      "Los <strong class='text-foreground'>informes de toxicología de impurezas firmados por toxicólogos EUROTOX</strong> son esenciales para evaluar riesgos e impurezas en la fabricación de medicamentos, vacunas, biológicos, ATMP, dispositivos médicos e IVD.",
      "Estas evaluaciones son cruciales para el personal de QA, responsables de cumplimiento, directores de planta y directores técnicos. Su importancia reside en apoyar los procedimientos de seguridad, asegurar el cumplimiento normativo y proteger tanto los productos como a los trabajadores en el proceso de fabricación.",
    ],
    whyTitle: "¿Cómo podemos ayudar?",
    whyBenefits: [
      { title: "Informes de evaluación de riesgos", text: "Evaluaciones de riesgos integrales para impurezas orgánicas en la fabricación farmacéutica." },
      { title: "Evaluaciones toxicológicas", text: "Evaluaciones que cubren nitrosaminas, impurezas orgánicas, impurezas mutagénicas, impurezas elementales y estudios E&L." },
      { title: "Mayor biblioteca online", text: "QbD Group ofrece la mayor biblioteca online de informes de toxicología de alta calidad certificados por miembros de AETOX." },
      { title: "Preparación para auditorías e inspecciones", text: "Repositorio documentado y actualizado de informes certificados para auditorías e inspecciones." },
    ],
    processTitle: "Nuestro proceso",
    processIntro: "Entregamos informes de toxicología certificados que cumplen los más altos estándares de calidad científica y cumplimiento normativo.",
    processSteps: [
      { title: "1. Consulta", text: "Comprender tus necesidades de evaluación de impurezas y requisitos regulatorios." },
      { title: "2. Revisión bibliográfica", text: "Revisión exhaustiva de datos toxicológicos para las impurezas identificadas." },
      { title: "3. Preparación del informe", text: "Informes certificados preparados y firmados por toxicólogos EUROTOX." },
      { title: "4. Entrega y soporte", text: "Entrega del informe con soporte continuo para consultas regulatorias." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestro equipo certificado EUROTOX entrega informes de toxicología certificados y de alta calidad, de confianza para clientes en todo el mundo.",
    partnerBullets: [
      "Soporte toxicológico integral — evaluaciones de riesgo integradas alineadas con estrategias de QA, CMC y regulatorias.",
      "Experiencia de confianza a escala — décadas de experiencia combinada en proyectos diversos.",
      "Supervisión proactiva del cumplimiento — anticipándose a las regulaciones de impurezas en evolución.",
      "Toxicólogos certificados — certificación EUROTOX (ERT) asegurando rigor científico.",
    ],
  },

  /* ───────── Residual Solvents ───────── */
  "residual-solvents-reports": {
    whatTitle: "¿Qué es la evaluación de riesgo de disolventes residuales?",
    whatParagraphs: [
      "La evaluación de riesgo de disolventes residuales es una <strong class='text-foreground'>evaluación integral de sustancias activas, excipientes y procesos de fabricación</strong> dirigida a identificar y mitigar los riesgos asociados con los disolventes residuales. Este proceso está diseñado para proteger a los pacientes de posibles efectos adversos.",
      "Incluye tres pasos clave:<ol class='list-decimal pl-6 mt-3 space-y-2'><li>Analizar los posibles disolventes residuales introducidos durante el proceso de fabricación.</li><li>Evaluar los niveles reales o previstos de disolventes en comparación con los valores PDE establecidos.</li><li>Definir una estrategia de control eficaz basada en los hallazgos.</li></ol>",
    ],
    whyTitle: "La importancia de gestionar los disolventes residuales",
    whyIntro: "Según la directriz ICH Q3C, los disolventes residuales no ofrecen beneficio terapéutico y pueden suponer riesgos potenciales para la seguridad del paciente.",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Los disolventes deben eliminarse en la mayor medida posible para proteger a los pacientes." },
      { title: "Cumplimiento normativo", text: "Cumplir las especificaciones del producto, GMP y otros requisitos basados en la calidad." },
      { title: "Preparación para auditorías", text: "Un repositorio documentado y actualizado asegura la preparación para las inspecciones regulatorias." },
      { title: "Calidad del producto", text: "Mantener la integridad del producto a lo largo del proceso de fabricación." },
    ],
    processTitle: "Cómo trabajamos: nuestro proceso para la evaluación de riesgo de disolventes residuales",
    processIntro: "En QbD Group seguimos un enfoque estructurado para asegurar una evaluación exhaustiva y conforme del riesgo de disolventes residuales.",
    processSteps: [
      { title: "1. Consulta inicial", text: "Comprender tus necesidades específicas y preocupaciones regulatorias." },
      { title: "2. Recopilación de datos", text: "Reunir documentación sobre el proceso de fabricación del producto farmacéutico." },
      { title: "3. Informe de evaluación de riesgos", text: "Informe detallado identificando fuentes potenciales de contaminación según las directrices de la EMA." },
      { title: "4. Entrega del informe", text: "Informe completo con niveles de disolventes, comparaciones PDE y controles requeridos." },
      { title: "5. Soporte de seguimiento", text: "Soporte continuo incluyendo cálculos de límites aceptables y límites de detección." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestro equipo certificado EUROTOX asegura los más altos estándares de calidad científica en cada evaluación de disolventes residuales.",
    partnerBullets: [
      "Soporte toxicológico integral — desde el desarrollo temprano hasta los cambios post-aprobación.",
      "Experiencia de confianza a escala — décadas de experiencia combinada en proyectos diversos.",
      "Supervisión proactiva del cumplimiento — anticipándose a las regulaciones de disolventes en evolución.",
      "Toxicólogos certificados — certificación EUROTOX (ERT) en cada evaluación.",
    ],
  },

  /* ───────── REACH ───────── */
  "reach-registration-compliance-support": {
    whatTitle: "¿Qué es el registro REACH?",
    whatParagraphs: [
      "Bajo REACH, todas las sustancias químicas fabricadas o importadas en la UE/EEE en cantidades de <strong class='text-foreground'>1 tonelada o más por año</strong> deben registrarse en la ECHA.",
      "La obligación generalmente se aplica a fabricantes de la UE/EEE, importadores de sustancias o mezclas de fuera de la UE/EEE y representantes que actúan en nombre de empresas no pertenecientes a la UE. El registro se basa en el principio de <strong class='text-foreground'>\"una sustancia, un registro\"</strong> y se presenta digitalmente a través del portal REACH utilizando IUCLID.",
    ],
    whyTitle: "¿Por qué es importante?",
    whyIntro: "Los datos precisos de identidad y composición de la sustancia son la base de un dossier exitoso.",
    whyBenefits: [
      { title: "Evitar pruebas duplicadas", text: "Una identificación adecuada ayuda a evitar costes innecesarios y estudios redundantes." },
      { title: "Evaluaciones de peligrosidad fiables", text: "Los datos precisos aseguran evaluaciones de riesgo y peligrosidad fiables." },
      { title: "Evaluación simplificada", text: "Tener los fundamentos correctos reduce las preguntas y retrasos post-presentación." },
      { title: "Eficiencia en costes", text: "Ahorra tiempo, dinero y esfuerzo a lo largo del proceso de registro." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "Sin importar en qué punto te encuentres de tu proceso REACH, podemos integrarnos con servicios dirigidos o entregar un programa completamente gestionado.",
      cards: [
        { title: "Registro de dossier REACH", text: "Preparación completa del dossier incluyendo SIP, identificación de sustancias y paquete de datos analíticos, perfil de composición cualitativo y cuantitativo, evaluación de impurezas alineada con el SIP, análisis de brechas de datos según los requisitos del Anexo VII-XI y entrada de datos en IUCLID." },
        { title: "Fichas de datos de seguridad (FDS)", text: "Redacción de FDS conforme a REACH según el Anexo II (16 secciones), incluyendo FDS ampliadas para sustancias que requieren escenarios de exposición. Cubre redacción, actualizaciones tras cambios en clasificación o datos de peligrosidad, y evaluaciones de bioseguridad cuando sea relevante." },
        { title: "Informe de seguridad química (CSR)", text: "Soporte integral de CSR incluyendo evaluación de peligrosidad (salud humana, fisicoquímica, ambiental, PBT/vPvB), evaluación de exposición y caracterización del riesgo, preparación y presentación del CSR en IUCLID, y actualizaciones basadas en nuevos datos o cambios regulatorios." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque estructurado para asegurar que tu registro REACH sea completo, preciso y conforme.",
    processSteps: [
      { title: "1. Caracterización de sustancias", text: "Definir SIP, datos analíticos y perfil de composición." },
      { title: "2. Análisis de brechas de datos", text: "Identificar información faltante o inadecuada según los requisitos del Anexo VII-XI." },
      { title: "3. Preparación del dossier", text: "Entrada de datos en IUCLID, carga de documentación y preparación del CSR." },
      { title: "4. Presentación y soporte", text: "Presentación digital a través del portal REACH con soporte continuo de cumplimiento." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Nuestro equipo combina experiencia regulatoria con profundo conocimiento toxicológico para asegurar tu cumplimiento REACH.",
    partnerBullets: [
      "Soporte toxicológico integral — desde la caracterización de sustancias hasta la presentación del dossier.",
      "Experiencia de confianza a escala — décadas de experiencia combinada en regulación química.",
      "Supervisión proactiva del cumplimiento — anticipándose a las actualizaciones regulatorias REACH.",
      "Toxicólogos certificados — miembros del equipo certificados EUROTOX (ERT).",
    ],
  },

  /* ───────── Monitoring Studies ───────── */
  "monitoring-studies": {
    whatTitle: "¿Cómo podemos ayudarte?",
    whatParagraphs: [
      "El <strong class='text-foreground'>servicio de estudios de seguimiento</strong> asiste en el seguimiento de estudios de laboratorio requeridos en la fabricación y calidad de medicamentos. Ofrecemos la experiencia técnica necesaria para validar métodos y ayudar a elegir el método más apropiado.",
      "Realizamos la revisión de resultados y asistimos en la redacción del informe final para su presentación a las autoridades regulatorias. Es importante mantenerse al día con los <strong class='text-foreground'>estándares</strong> para que tus procesos <strong class='text-foreground'>cumplan con las regulaciones vigentes</strong> aprobadas por las autoridades regulatorias.",
    ],
    whyTitle: "¿Por qué QbD Group?",
    whyBenefits: [
      { title: "Soporte toxicológico integral", text: "Desde el desarrollo temprano hasta los cambios post-aprobación, evaluaciones de riesgo integradas alineadas con estrategias de QA, CMC y regulatorias." },
      { title: "Experiencia de confianza a escala", text: "Décadas de experiencia combinada proporcionando insights fiables en proyectos diversos." },
      { title: "Supervisión proactiva del cumplimiento", text: "Control estricto para mantener a los clientes por delante de las regulaciones en evolución." },
      { title: "Toxicólogos certificados", text: "Toxicólogos certificados EUROTOX (ERT) asegurando los más altos estándares científicos." },
    ],
    processTitle: "Nuestro enfoque",
    processIntro: "Nuestro equipo de expertos altamente cualificados colabora contigo en cada etapa, asegurando resultados meticulosos y fiables.",
    processSteps: [
      { title: "1. Definición del proyecto", text: "Definir los requisitos y objetivos del estudio de seguimiento." },
      { title: "2. Validación de métodos", text: "Ayudar a elegir y validar los métodos analíticos más apropiados." },
      { title: "3. Revisión de resultados", text: "Revisión exhaustiva de los resultados de laboratorio e interpretación de datos." },
      { title: "4. Redacción de informes", text: "Asistencia en la redacción del informe final para presentación regulatoria." },
    ],
    partnerTitle: "Asóciate con QbD Group",
    partnerIntro: "Ya necesites soporte con seguimiento de laboratorio, validación de métodos o certificación de seguridad ambiental, nuestros expertos están aquí para ayudarte.",
    partnerBullets: [
      "Soporte en investigación científica — enfoque meticuloso y orientado al detalle para resultados precisos.",
      "Experiencia en pruebas de calidad — capacidades integrales de validación y pruebas.",
      "Soporte en presentaciones regulatorias — informes preparados según los requisitos de las autoridades regulatorias.",
      "Toxicólogos certificados — equipo certificado EUROTOX asegurando los más altos estándares.",
    ],
  },
};

const offeringContentZh: Record<string, OfferingSectionTextOverride> = {
  "residual-solvents-reports": {
    whatTitle: "什么是残留溶剂风险评估？",
    whatParagraphs: [
      "残留溶剂风险评估是对<strong class='text-foreground'>原料药、辅料和生产工艺</strong>的全面评估，旨在识别和降低与残留溶剂相关的风险。该流程旨在保护患者免受潜在的不良影响。",
      "包括三个关键步骤：<ol class='list-decimal pl-6 mt-3 space-y-2'><li>分析生产过程中可能引入的残留溶剂。</li><li>将实际或预测的溶剂水平与既定的 PDE 值进行对比评估。</li><li>根据结果制定有效的控制策略。</li></ol>",
    ],
    whyTitle: "管理残留溶剂的重要性",
    whyIntro: "根据 ICH Q3C 指南，残留溶剂没有治疗益处，可能对患者的安全构成潜在风险。",
    whyBenefits: [
      { title: "患者安全", text: "必须尽可能去除溶剂，以保护患者。" },
      { title: "法规合规", text: "满足产品规格、GMP 及其他基于质量的要求。" },
      { title: "审计准备", text: "更新且记录完整的资料库确保为监管检查做好准备。" },
      { title: "产品质量", text: "在整个生产过程中保持产品完整性。" },
    ],
    processTitle: "我们的工作方式：残留溶剂风险评估流程",
    processIntro: "在 QbD Group，我们采用结构化方法，确保对残留溶剂进行彻底且合规的风险评估。",
    processSteps: [
      { title: "1. 初步咨询", text: "了解您的具体需求和法规关切。" },
      { title: "2. 数据收集", text: "收集有关药品生产工艺的文件资料。" },
      { title: "3. 风险评估报告", text: "详细报告，根据 EMA 指南识别潜在污染源。" },
      { title: "4. 报告交付", text: "包含溶剂水平、PDE 对比和所需控制措施的综合报告。" },
      { title: "5. 后续支持", text: "持续支持，包括可接受限度计算和检测限度。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的 EUROTOX 认证团队确保每一次残留溶剂评估都达到最高的科学质量标准。",
    partnerBullets: [
      "端到端的毒理学支持——从早期开发到上市后变更。",
      "可信赖的大规模专业能力——在各类项目中拥有数十年的综合经验。",
      "主动的合规监控——紧跟不断演变的溶剂法规。",
      "认证毒理学专家——每次评估均由 EUROTOX 认证(ERT)毒理学家执行。",
    ],
  },
  "reach-registration-compliance-support": {
    whatTitle: "什么是 REACH 注册？",
    whatParagraphs: [
      "根据 REACH 法规，所有在欧盟/欧洲经济区(EU/EEA)制造或进口的、年产量或进口量达到<strong class='text-foreground'>1 吨及以上</strong>的化学物质，均须在 ECHA 进行注册。",
      "该义务通常适用于欧盟/欧洲经济区的制造商、来自欧盟/欧洲经济区以外的物质或混合物的进口商，以及代表非欧盟公司的唯一代表。注册遵循<strong class='text-foreground'>“一物质，一注册”</strong>的原则，并通过 REACH 门户使用 IUCLID 以数字方式提交。",
    ],
    whyTitle: "为什么这很重要？",
    whyIntro: "准确的物质身份和组成数据是成功卷宗的基础。",
    whyBenefits: [
      { title: "避免重复测试", text: "正确的识别有助于避免不必要的成本和重复研究。" },
      { title: "可靠的危害评估", text: "准确的数据可确保危害和风险评估的可靠性。" },
      { title: "简化评估流程", text: "夯实基础可减少提交后的问询和延误。" },
      { title: "节省成本", text: "在整个注册过程中节省时间、金钱和精力。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助？",
      intro: "无论您处于 REACH 流程的哪个阶段，我们都可以提供定向服务，或交付完全托管的全流程项目。",
      cards: [
        { title: "REACH 卷宗注册", text: "完整的卷宗准备工作，包括 SIP、物质识别和分析数据包、定性和定量组成概况、与 SIP 一致的杂质评估、依据附录 VII-XI 要求的数据差距分析，以及 IUCLID 数据录入。" },
        { title: "安全数据表(SDS)", text: "依据附录 II(16 个章节)起草符合 REACH 要求的 SDS，包括需要暴露场景的物质的扩展 SDS。涵盖起草工作、在分类或危害数据发生变更后的更新，以及在相关情况下的生物安全评估。" },
        { title: "化学品安全报告(CSR)", text: "全面的 CSR 支持，包括危害评估(人体健康、理化、环境、PBT/vPvB)、暴露评估和风险表征、在 IUCLID 中编制和提交 CSR，以及基于新数据或法规变更的更新。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "我们采用结构化方法，确保您的 REACH 注册完整、准确且合规。",
    processSteps: [
      { title: "1. 物质表征", text: "界定 SIP、分析数据和组成概况。" },
      { title: "2. 数据差距分析", text: "根据附录 VII-XI 的要求识别缺失或不足的信息。" },
      { title: "3. 卷宗准备", text: "在 IUCLID 中录入数据、上传文档并准备 CSR。" },
      { title: "4. 提交与支持", text: "通过 REACH 门户进行数字化提交，并提供持续的合规支持。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的团队将法规专业能力与深厚的毒理学知识相结合，确保您的 REACH 合规。",
    partnerBullets: [
      "端到端的毒理学支持——从物质表征到卷宗提交。",
      "可信赖的大规模专业能力——在化学品监管领域拥有数十年的综合经验。",
      "主动的合规监控——紧跟 REACH 法规更新。",
      "认证毒理学专家——团队成员均持有 EUROTOX 认证(ERT)。",
    ],
  },
  "pde-oel-reports": {
    whatTitle: "符合行业标准的 PDE/ADE 报告",
    whatParagraphs: [
      "我们经验丰富的团队依据 <strong class='text-foreground'>EMA 指南(EMA/CHMP/CVMP/SWP/169430/2012)</strong>，为任何活性药物成分(API)编制 <strong class='text-foreground'>PDE/ADE(每日允许暴露量/每日可接受暴露量)报告</strong>。这些报告对于制药和兽药行业确保产品安全和法规合规至关重要。",
      "让 QbD Group 通过科学严谨的评估，为您提供准确、可靠的 PDE 数值。",
    ],
    whyTitle: "为什么选择 QbD Group 编制您的 PDE 报告？",
    whyIntro: "在 PDE/ADE 报告方面，您需要一位将科学专业能力、可靠业绩和质量承诺融为一体的合作伙伴。",
    whyBenefits: [
      { title: "丰富的经验", text: "已评估超过 3,500 种化合物。" },
      { title: "全球信赖", text: "全球 600 多家客户的认可。" },
      { title: "国际覆盖", text: "服务遍及 50 多个国家。" },
      { title: "高素质团队", text: "由经验丰富的 ERT 认证毒理学家编制和审核。" },
      { title: "稳健的评估", text: "科学严谨的评估结合专家判断。" },
      { title: "深入的分析", text: "对 PDE 数值推导提供详尽论证。" },
      { title: "可靠的结果", text: "基于证据、为您量身定制的精确且可靠的 PDE 数值。" },
    ],
    processTitle: "我们的流程",
    processIntro: "我们采用结构化方法，交付符合最高科学质量和法规合规标准的 PDE/ADE 报告。",
    processSteps: [
      { title: "1. 初步咨询", text: "了解您的具体需求和法规关切。" },
      { title: "2. 数据收集", text: "收集化合物及其毒理学特征的必要文件。" },
      { title: "3. 毒理学评估", text: "全面的文献回顾和专家评估，推导 PDE 数值。" },
      { title: "4. 报告交付", text: "提供包含完整科学依据的详尽 PDE/ADE 报告。" },
      { title: "5. 后续支持", text: "为法规问询或更新提供持续协助。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的报告由 EUROTOX 认证(ERT)毒理学家编制和审核，确保最高水平的科学质量、法规可靠性和患者安全。",
    partnerBullets: [
      "端到端的毒理学支持——从早期开发到上市后变更。",
      "可信赖的大规模专业能力——在各类项目和市场中拥有数十年的综合经验。",
      "主动的合规监控——严格把控，紧跟不断演变的法规。",
      "认证毒理学专家——每个项目均有 EUROTOX 认证(ERT)。",
    ],
  },
  "organic-impurities-reports": {
    whatTitle: "为什么有必要？",
    whatParagraphs: [
      "由 <strong class='text-foreground'>EUROTOX 毒理学家签署的毒理学杂质报告</strong>对于评估药品、疫苗、生物制品、ATMP、Medical Devices 和 IVD 生产中的风险和杂质至关重要。",
      "这些评估对 QA 人员、合规官、现场总监和技术总监必不可少。其重要性在于支持安全程序、确保法规合规，并在生产过程中保护产品和工作人员。",
    ],
    whatImageAlt: "有机杂质报告 — QbD Group",
    whyTitle: "我们如何帮助您？",
    whyBenefits: [
      { title: "风险评估报告", text: "对药品制造中有机杂质的全面风险评估。" },
      { title: "毒理学评估", text: "涵盖亚硝胺、有机杂质、致突变杂质、元素杂质以及 E&L 研究的评估。" },
      { title: "最大的在线报告库", text: "QbD Group 提供由 AETOX 成员认证的高质量毒理学报告在线库。" },
      { title: "审计与检查准备", text: "为审计和检查提供完备的最新认证报告库。" },
    ],
    processTitle: "我们的流程",
    processIntro: "我们提供符合最高科学质量和法规合规标准的认证毒理学报告。",
    processSteps: [
      { title: "1. 咨询", text: "了解您的杂质评估需求和法规要求。" },
      { title: "2. 文献综述", text: "对已识别杂质的毒理学数据进行全面审查。" },
      { title: "3. 报告编制", text: "由 EUROTOX 毒理学家编制和签署的认证报告。" },
      { title: "4. 交付与支持", text: "交付报告，并为法规问询提供持续支持。" },
    ],
    processImageAlt: "有机杂质流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的 EUROTOX 认证团队提供高质量、经认证的毒理学报告，深受全球客户信赖。",
    partnerBullets: [
      "端到端的毒理学支持——与 QA、CMC 和法规策略相整合的风险评估。",
      "可信赖的大规模专业能力——在各类项目中拥有数十年的综合经验。",
      "主动的合规监控——紧跟不断演变的杂质法规。",
      "认证的毒理学专家——EUROTOX 认证(ERT)毒理学家确保科学严谨性。",
    ],
  },
  "nitrosamine-risk-assessment": {
    whatTitle: "什么是亚硝胺风险评估？",
    whatParagraphs: [
      "亚硝胺风险评估是对<strong class='text-foreground'>药品生产工艺</strong>的全面评估，旨在识别和降低与亚硝胺杂质相关的风险。该评估涉及分析原材料、辅料、生产设备和工艺流程。",
      "这一过程遵循全球法规指南(EMA、FDA)，<strong class='text-foreground'>确保所有检出的亚硝胺水平均保持在可接受限度内</strong>。这是上市许可持有人(MAH)、原料药(API)制造商、成品药制造商和供应商的基本要求。",
    ],
    whatImageAlt: "亚硝胺风险评估 — 毒理学 — QbD Group",
    whyTitle: "为什么这很重要？",
    whyIntro: "亚硝胺杂质因其高致癌潜力而成为重大的安全问题，在致突变杂质中被列为\"关注队列(cohort of concern)\"。",
    whyBenefits: [
      { title: "高致癌潜力", text: "亚硝胺具有显著的致癌风险，并受到严格的法规限度限制。" },
      { title: "法规合规", text: "企业必须识别、评估和降低风险，以符合 EMA、FDA 和 ICH 指南的要求。" },
      { title: "避免昂贵的召回", text: "适当的评估可防止不合规处罚、产品召回或延误。" },
      { title: "持续的法规审查", text: "新数据不断涌现，要求评估保持最新状态。" },
    ],
    helpCards: {
      heading: "我们如何帮助您？",
      intro: "无论您的亚硝胺风险评估(NRA)需求是什么，我们都提供多种服务来支持您：",
      cards: [
        { title: "风险评估", text: "对生产工艺进行全面评估，涵盖 EMA 和 FDA 指南中描述的所有风险因素和根本原因。当出现需要反映在报告中的新信息时，及时更新 NRA。" },
        { title: "可接受限度计算", text: "协助根据可接受摄入量(AI)计算确定可接受限度，适用于被识别为潜在风险的亚硝胺杂质。涵盖 EMA 和 FDA 列出的亚硝胺，以及未知亚硝胺。包括支持 CPCA AI 的建立，并探索确定可接受摄入量的不同选项。" },
        { title: "NRA 报告审核", text: "评估其他公司编制的 NRA 报告或与药品组件中亚硝胺杂质相关的文件，以确保符合监管机构的要求。" },
        { title: "专家支持", text: "支持实施由亚硝胺风险评估结果衍生的各项措施，确保采取适当的控制步骤，有效应对已识别的风险。" },
        { title: "优先策略", text: "分析多种药品的生产工艺，识别最容易受到亚硝胺杂质风险影响的产品，并提出优先排序策略。" },
        { title: "亚硝胺毒理学评估报告", text: "毒理学评价，包括遗传毒性/致癌性数据审查，以及使用具有充分致癌性数据的替代物的 TD50 值作为起点，通过 SAR 和交叉参照(read-across)方法推导可接受摄入量(AI)。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "我们遵循结构化、分步推进的方法，确保全面的亚硝胺风险管理。",
    processSteps: [
      { title: "1. 初步咨询", text: "了解客户的具体需求和法规关切。" },
      { title: "2. 数据收集", text: "收集药品生产工艺相关文件，包括制造商声明。" },
      { title: "3. 风险评估报告", text: "编制 NRA 报告，识别亚硝胺污染的潜在来源。" },
      { title: "4. 报告交付", text: "提供详尽的报告，说明是否存在亚硝胺风险。" },
      { title: "5. 后续支持", text: "持续协助，包括为确认性检测提供可接受限度计算。" },
    ],
    processImageAlt: "亚硝胺风险评估流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的报告由 EUROTOX 认证(ERT)毒理学家编制和审核，确保最高水平的科学质量和法规可靠性。",
    partnerBullets: [
      "端到端的毒理学支持——与 QA、CMC 和法规策略相整合的风险评估。",
      "可信赖的大规模专业能力——在各类项目和市场中的可靠洞见。",
      "主动的合规监控——紧跟不断演变的亚硝胺法规。",
      "认证的毒理学专家——每次评估均由 EUROTOX 认证(ERT)毒理学家执行。",
    ],
  },
  "monitoring-studies": {
    whatTitle: "我们如何帮助您？",
    whatParagraphs: [
      "<strong class='text-foreground'>监测研究服务</strong>协助跟踪药品生产和质量所需的实验室研究。我们提供必要的技术专长来验证方法，并帮助选择最合适的方法。",
      "我们对结果进行审核，并协助撰写最终报告，以便提交给监管机构。及时掌握<strong class='text-foreground'>标准</strong>的最新动态非常重要，这样您的流程才能<strong class='text-foreground'>符合监管机构批准的现行法规</strong>要求。",
    ],
    whatImageAlt: "监测研究 — QbD Group",
    whyTitle: "为什么选择 QbD Group？",
    whyBenefits: [
      { title: "端到端毒理学支持", text: "从早期开发到上市后变更，与 QA、CMC 和法规策略相整合的综合风险评估。" },
      { title: "可信赖的大规模专业能力", text: "数十年的综合经验，在各类项目中提供可靠的洞见。" },
      { title: "主动的合规监控", text: "严格监督，帮助客户走在不断演变的法规前列。" },
      { title: "认证毒理学专家", text: "EUROTOX 认证(ERT)毒理学家确保最高的科学标准。" },
    ],
    processTitle: "我们的方法",
    processIntro: "我们高素质的专家团队在每个阶段与您协作，确保细致且可靠的结果。",
    processSteps: [
      { title: "1. 项目范围界定", text: "确定监测研究的需求和目标。" },
      { title: "2. 方法验证", text: "帮助选择并验证最合适的分析方法。" },
      { title: "3. 结果审核", text: "全面审核实验室结果并解读数据。" },
      { title: "4. 报告撰写", text: "协助撰写最终报告，用于法规申报。" },
    ],
    processImageAlt: "监测研究流程 — QbD Group",
    partnerTitle: "与 QbD Group 合作",
    partnerIntro: "无论您需要实验室跟踪支持、方法验证还是环境安全认证，我们的专家都将竭诚为您服务。",
    partnerBullets: [
      "科学研究支持——以细致严谨的态度确保准确结果。",
      "质量测试专业能力——全面的验证和测试能力。",
      "法规申报支持——按照监管机构要求准备的报告。",
      "认证毒理学专家——EUROTOX 认证团队确保最高标准。",
    ],
  },
  "extractables-leachables-reports": {
    whatTitle: "什么是可提取物与浸出物报告？",
    whatParagraphs: [
      "可提取物和浸出物杂质的风险和毒理学评估是制药行业中确保产品安全性和质量的关键流程。",
      "这些杂质可能从包装材料或医疗器械迁移到最终产品中，<strong class='text-foreground'>对患者构成潜在风险</strong>。FDA 和 EMA 等监管机构<strong class='text-foreground'>要求提供详细报告</strong>，以支持药品和医疗器械的安全性。",
    ],
    whatImageAlt: "可提取物与浸出物报告 — QbD Group",
    whyTitle: "我们如何帮助您？",
    whyIntro: "我们的毒理学和分析化学团队密切协作，进行深度分析、风险评估和毒理学评估。",
    whyBenefits: [
      { title: "风险评估", text: "分析可能从与药品接触的材料中释放的化合物，评估其对安全性的影响，并确定安全限度。" },
      { title: "毒理学评估", text: "针对超出既定限度的所有杂质——收集有害效应数据，确定毒性，并使用 QSAR 方法评估健康风险。" },
      { title: "定制化报告", text: "根据每个药品和客户特定的法规要求定制的报告。" },
      { title: "法规一致性", text: "符合 FDA、EMA、USP 1663、USP 1664 和 ICH Q3E 指南。" },
    ],
    processTitle: "我们的流程",
    processIntro: "我们提供符合每种药品和客户特定法规要求的定制化报告。",
    processSteps: [
      { title: "1. 初步咨询", text: "了解您的具体材料和法规背景。" },
      { title: "2. 材料分析", text: "识别包装材料和器械中可能的可提取物和浸出物化合物。" },
      { title: "3. 风险与毒理学评估", text: "评估对安全性的影响，并确定杂质是否在安全限度内。" },
      { title: "4. 报告编制", text: "交付全面、可随时提交的、符合法规标准的报告。" },
    ],
    processImageAlt: "可提取物与浸出物流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们将深厚的技术知识与对国际法规的详尽理解相结合，确保提供定制化且严谨的报告。",
    partnerBullets: [
      "由毒理学专家和分析化学家组成的专业多学科团队。",
      "根据每个客户的法规要求和具体需求定制的报告。",
      "将深厚的技术知识与对国际法规的理解相结合的方法。",
      "AETOX 成员和 EUROTOX 认证——最高专业标准。",
    ],
  },
  "environmental-risk-assessment-era": {
    whatTitle: "环境风险评估在上市许可中的作用",
    whatParagraphs: [
      "环境风险评估并非可选附加项；在许多地区，尤其是环境暴露可能发生变化的新上市许可申请(MAA)中，这是一项<strong class='text-foreground'>强制性要求</strong>。",
      "ERA 帮助监管机构<strong class='text-foreground'>评估对水生和陆地生态系统造成危害的可能性</strong>，依据预测环境浓度和生态毒理学数据。",
      "如果完成得当，ERA 报告可为暴露估算和检测决策提供透明的科学依据，<strong class='text-foreground'>帮助避免不必要的研究，并促进更顺畅的监管互动</strong>。将文献数据、归宿研究和精细化的暴露评估整合为一份连贯的、可直接申报的文件，是满足监管期望和避免延误的关键。",
    ],
    whatImageAlt: "环境风险评估 (ERA) — QbD Group",
    whyTitle: "QbD Group 如何支持您的环境风险评估",
    whyIntro: "结构清晰、科学驱动的 ERA 支持，与法规期望保持一致。我们在 ERA 全生命周期为制药企业提供支持，将科学专业知识与务实、基于风险的方法相结合。",
    whyBenefits: [
      { title: "全面的数据收集", text: "涵盖使用数据、理化性质、环境归宿和生态毒理学。" },
      { title: "暴露与风险评估", text: "PEC 计算与精细化、PNEC 推导以及 PBT/vPvB 筛选。" },
      { title: "科学依据", text: "支持针对性评估、检测豁免或依据 EMA 指南进行的精细化调整。" },
      { title: "清晰、结构化的 ERA 报告", text: "专为直接纳入 CTD 模块 1.6 而设计，并与法规期望保持一致。" },
    ],
    extraSections: [
      {
        title: "从早期范围界定到可直接申报的 ERA 报告",
        paragraphs: [
          "我们从早期范围界定开始，就 API、目标市场、适应症和可用数据达成一致，<strong class='text-foreground'>确保 ERA 策略从一开始就与法规背景相匹配</strong>。",
          "我们的团队采用<strong class='text-foreground'>分阶段的方法</strong>：初步 Tier A PEC 计算，使用特定国家的流行率和消费数据进行精细化，并整合文献和历史数据以支持科学严谨的结论。在适当情况下，我们准备<strong class='text-foreground'>明确的科学依据以豁免不相关的研究</strong>，或避免升级到更高的评估层级。",
          "每个项目最终以一份<strong class='text-foreground'>可直接申报的 ERA 报告</strong>作为结论，该报告完全可追溯、文档透明，并准备接受监管审查。",
        ],
      },
      {
        title: "为客户带来的价值",
        paragraphs: [],
        bullets: [
          { title: "监管信心", text: "按照 EMA 指南构建的 ERA 报告，支持档案的顺利审查和接受。" },
          { title: "减少不必要的检测", text: "战略性利用现有数据、精细化调整和科学依据，避免不相关的研究。" },
          { title: "效率与可预测性", text: "清晰的工作流程和早期风险识别有助于防止后期出现意外和延误。" },
          { title: "检查就绪的文档", text: "透明的计算、完整的参考文献和可追溯的决策过程，与监管审查保持一致。" },
        ],
      },
    ],
    processTitle: "我们的流程",
    processIntro: "我们遵循与 EMA 指南一致的分阶段、科学驱动的方法，交付可直接申报的 ERA 报告。",
    processSteps: [
      { title: "1. 早期范围界定", text: "就 API、目标市场、适应症和可用数据达成一致。" },
      { title: "2. 数据收集", text: "对使用、理化、归宿和生态毒理学数据进行全面的技术评估。" },
      { title: "3. 暴露评估", text: "PEC 计算、精细化调整和 PNEC 推导。" },
      { title: "4. 报告编制", text: "可直接申报的 CTD 模块 1.6 ERA 报告。" },
    ],
    processImageAlt: "ERA 流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的 EUROTOX 认证团队提供高效、科学驱动的 ERA 支持，与法规期望保持一致。",
    partnerBullets: [
      "端到端的毒理学支持——从早期开发到上市后变更，我们提供与 QA、CMC 和法规策略无缝整合的综合风险评估。",
      "可信赖的大规模专业能力——在各类项目和市场中拥有数十年的综合经验，我们的毒理学团队提供可靠的洞见，为每次评估增添效率、一致性和信心。",
      "主动的合规监控——通过对报告的严格监督，我们帮助客户全面掌控毒理学义务，并在潜在不合规演变为风险之前主动发出预警。",
      "认证毒理学专家——我们的报告由 EUROTOX 认证(ERT)毒理学家编制和审核，确保每次评估都达到最高的科学质量、法规可靠性和患者安全标准。",
    ],
  },
  "elemental-impurities-reports": {
    whatTitle: "什么是元素杂质风险评估？",
    whatParagraphs: [
      "确保符合元素杂质法规要求，首先要对药品生产工艺进行彻底的风险评估。在 QbD Group，我们引导您完成<strong class='text-foreground'>风险评估的四个关键步骤</strong>：",
      "<ol class='list-decimal pl-6 mt-3 space-y-2'><li>识别元素杂质的潜在来源。</li><li>分析每种污染源引入的元素。</li><li>将实际或预测的水平与既定的 PDE 值进行对比评估。</li><li>根据结果制定控制策略。</li></ol>",
      "我们的专家团队确保采用全面的方法，帮助您满足法规标准，同时保障产品质量和患者安全。",
    ],
    whatImageAlt: "元素杂质报告 — QbD Group",
    whyTitle: "为什么控制元素杂质很重要",
    whyIntro: "元素杂质没有治疗益处，必须控制在可接受限度内，如 ICH Q3D 指南所述。",
    whyBenefits: [
      { title: "法规要求", text: "上市许可申请(MAA)和 GMP 检查的强制性要求。" },
      { title: "患者安全", text: "控制杂质可保护患者免受潜在健康风险。" },
      { title: "审计准备", text: "记录在案的风险评估是审计和检查的必备要求。" },
      { title: "注册申报资料", text: "报告必须纳入法规申报文件。" },
    ],
    processTitle: "从咨询到合规：我们的分步流程",
    processIntro: "我们采用结构化的方法，确保对药品中元素杂质进行顺畅且合规的评估。",
    processSteps: [
      { title: "1. 初步咨询", text: "了解您的具体需求和法规关切，以量身定制我们的方法。" },
      { title: "2. 数据收集", text: "收集文件资料，包括生产工艺细节和制造商声明。" },
      { title: "3. 风险评估报告", text: "详细报告，识别污染源并根据 EMA/FDA 指南评估风险。" },
      { title: "4. 报告交付", text: "包含杂质水平、PDE 对比和控制建议的综合报告。" },
      { title: "5. 后续支持", text: "持续协助，包括可接受限度计算和检测限度建议。" },
    ],
    processImageAlt: "元素杂质流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们的 EUROTOX 认证团队确保每一次元素杂质评估都达到最高的科学质量和法规可靠性标准。",
    partnerBullets: [
      "端到端的毒理学支持——从早期开发到上市后变更。",
      "可信赖的大规模专业能力——在各类项目中拥有数十年的综合经验。",
      "主动的合规监控——紧跟不断演变的法规。",
      "认证毒理学专家——每个项目均由 EUROTOX 认证(ERT)毒理学家执行。",
    ],
  },
};

/**
 * Returns localised offering section data by merging language-specific text overrides
 * onto the English base data. Icons and images are preserved from the base.
 */
export const getLocalizedToxOfferingData = (
  contentKey: string,
  baseData: OfferingSection,
  lang: SiteLang,
): OfferingSection => {
  if (lang === "en") return baseData;
  if (lang === "zh-cn") {
    const override = offeringContentZh[contentKey];
    if (!override) return baseData;
    return {
      ...baseData,
      whatTitle: override.whatTitle,
      whatParagraphs: override.whatParagraphs,
      ...(override.whatImageAlt ? { whatImageAlt: override.whatImageAlt } : {}),
      whyTitle: override.whyTitle,
      whyIntro: override.whyIntro,
      whyBenefits: baseData.whyBenefits.map((b, i) =>
        override.whyBenefits[i]
          ? { ...b, title: override.whyBenefits[i].title, text: override.whyBenefits[i].text }
          : b
      ),
      ...(override.helpCards && baseData.helpCards
        ? {
            helpCards: {
              ...baseData.helpCards,
              heading: override.helpCards.heading,
              intro: override.helpCards.intro,
              cards: baseData.helpCards.cards.map((c, i) =>
                override.helpCards!.cards[i]
                  ? { ...c, title: override.helpCards!.cards[i].title, text: override.helpCards!.cards[i].text }
                  : c
              ),
            },
          }
        : {}),
      ...(override.extraSections && baseData.extraSections
        ? {
            extraSections: baseData.extraSections.map((s, i) => {
              const zhSection = override.extraSections![i];
              if (!zhSection) return s;
              return {
                ...s,
                title: zhSection.title,
                paragraphs: zhSection.paragraphs,
                ...(zhSection.bullets && s.bullets
                  ? {
                      bullets: s.bullets.map((b, j) =>
                        zhSection.bullets![j]
                          ? { ...b, title: zhSection.bullets![j].title, text: zhSection.bullets![j].text }
                          : b
                      ),
                    }
                  : {}),
              };
            }),
          }
        : {}),
      ...(override.processTitle ? { processTitle: override.processTitle } : {}),
      ...(override.processIntro ? { processIntro: override.processIntro } : {}),
      ...(override.processSteps && baseData.processSteps
        ? {
            processSteps: baseData.processSteps.map((s, i) =>
              override.processSteps![i]
                ? { ...s, title: override.processSteps![i].title, text: override.processSteps![i].text }
                : s
            ),
          }
        : {}),
      ...(override.processImageAlt ? { processImageAlt: override.processImageAlt } : {}),
      partnerTitle: override.partnerTitle,
      partnerIntro: override.partnerIntro,
      partnerBullets: override.partnerBullets,
    };
  }
  const override = offeringContentES[contentKey];
  if (!override) return baseData;

  return {
    ...baseData,
    whatTitle: override.whatTitle,
    whatParagraphs: override.whatParagraphs,
    ...(override.whatImageAlt ? { whatImageAlt: override.whatImageAlt } : {}),
    whyTitle: override.whyTitle,
    whyIntro: override.whyIntro,
    whyBenefits: baseData.whyBenefits.map((b, i) =>
      override.whyBenefits[i]
        ? { ...b, title: override.whyBenefits[i].title, text: override.whyBenefits[i].text }
        : b
    ),
    ...(override.helpCards && baseData.helpCards
      ? {
          helpCards: {
            ...baseData.helpCards,
            heading: override.helpCards.heading,
            intro: override.helpCards.intro,
            cards: baseData.helpCards.cards.map((c, i) =>
              override.helpCards!.cards[i]
                ? { ...c, title: override.helpCards!.cards[i].title, text: override.helpCards!.cards[i].text }
                : c
            ),
          },
        }
      : {}),
    ...(override.extraSections && baseData.extraSections
      ? {
          extraSections: baseData.extraSections.map((s, i) => {
            const esSection = override.extraSections![i];
            if (!esSection) return s;
            return {
              ...s,
              title: esSection.title,
              paragraphs: esSection.paragraphs,
              ...(esSection.bullets && s.bullets
                ? {
                    bullets: s.bullets.map((b, j) =>
                      esSection.bullets![j]
                        ? { ...b, title: esSection.bullets![j].title, text: esSection.bullets![j].text }
                        : b
                    ),
                  }
                : {}),
            };
          }),
        }
      : {}),
    ...(override.processTitle ? { processTitle: override.processTitle } : {}),
    ...(override.processIntro ? { processIntro: override.processIntro } : {}),
    ...(override.processSteps && baseData.processSteps
      ? {
          processSteps: baseData.processSteps.map((s, i) =>
            override.processSteps![i]
              ? { ...s, title: override.processSteps![i].title, text: override.processSteps![i].text }
              : s
          ),
        }
      : {}),
    ...(override.processImageAlt ? { processImageAlt: override.processImageAlt } : {}),
    partnerTitle: override.partnerTitle,
    partnerIntro: override.partnerIntro,
    partnerBullets: override.partnerBullets,
  };
};

/** UI chrome for Tox offering pages */
const toxOfferingUI = {
  en: {
    faqTitle: "Frequently Asked Questions",
    toxicology: "Regulatory Toxicology",
  },
  es: {
    faqTitle: "Preguntas frecuentes",
    toxicology: "Toxicología Regulatoria",
  },
  "zh-cn": {
    faqTitle: "常见问题",
    toxicology: "法规毒理学",
  },
};

export const getToxOfferingUI = (lang: SiteLang) => toxOfferingUI[lang] ?? toxOfferingUI.en;
